// apps/web/src/features/users/components/UsersListPage.tsx
// Spec §14.2 / plan Task 5.6. ADMIN-gated list screen assembling FiltersBar
// + UsersTable + modal/drawer state. Row click + actions navigate to
// /users/:id for editing; other actions open local confirm dialogs.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { RoleId, User } from '@magimp/shared';
import { useUsersList, useRolesList, useDepartmentsList } from '@magimp/shared-hooks';
import {
  Button,
  FiltersBar,
  EmptyState,
  ErrorState,
  LoadingState,
  ToolbarActions,
  type SelectOption,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useAuthStore } from '../../../stores/auth.js';
import { UsersTable } from './UsersTable.js';
import { UserCreateModal } from './UserCreateModal.js';
import { UserEditDrawer } from './UserEditDrawer.js';
import { UserDeactivateConfirm } from './UserDeactivateConfirm.js';
import { UserReactivateConfirm } from './UserReactivateConfirm.js';
import { UserResetPasswordModal } from './UserResetPasswordModal.js';

type Filters = { roleId: '' | RoleId; active: '' | 'true' | 'false' };

export function UsersListPage() {
  const { t } = useTranslation(['users', 'common']);
  const navigate = useNavigate();
  const { id: drawerId } = useParams<{ id?: string }>();
  const current = useAuthStore((s) => s.user);
  const [filters, setFilters] = useState<Filters>({ roleId: '', active: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<User | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  const rolesQ = useRolesList();
  const roleOptions: SelectOption[] = (rolesQ.data ?? []).map((r) => ({
    value: r.id,
    label: r.displayName,
  }));
  const activeOptions: SelectOption[] = [
    { value: 'true', label: t('users:filters.active') },
    { value: 'false', label: t('users:filters.inactive') },
  ];

  const activeMap: Record<'' | 'true' | 'false', undefined | boolean> = {
    '': undefined,
    true: true,
    false: false,
  };
  const queryFilter: { roleId?: RoleId; active?: boolean } = {};
  if (filters.roleId) queryFilter.roleId = filters.roleId;
  const activeValue = activeMap[filters.active];
  if (activeValue !== undefined) queryFilter.active = activeValue;

  const usersQuery = useUsersList(queryFilter);
  // WHY: load all departments (active + archived) so historical rows whose
  // department was archived still resolve to a name in the table cell. Empty
  // map while loading; UsersTable falls back to DEPARTMENT_FALLBACK per cell.
  const departmentsQuery = useDepartmentsList();
  const departmentsById: Record<string, string> = {};
  for (const d of departmentsQuery.data ?? []) {
    departmentsById[d.id] = d.name;
  }

  const openEdit = (u: User) => navigate(`/users/${u.id}`);
  const openDeactivate = (u: User) => setDeactivateTarget(u);
  const openReactivate = (u: User) => setReactivateTarget(u);
  const openResetPassword = (u: User) => setResetTarget(u);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1
          style={{
            fontSize: 20,
            fontWeight: 600,
            margin: 0,
            flex: 1,
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {t('users:title')}
        </h1>
        <ToolbarActions>
          <Button icon="plus" kind="primary" onClick={() => setCreateOpen(true)}>
            {t('users:actions.create')}
          </Button>
        </ToolbarActions>
      </header>
      <FiltersBar
        filters={[
          { type: 'select', key: 'roleId', label: t('users:fields.role'), options: roleOptions },
          {
            type: 'select',
            key: 'active',
            label: t('users:fields.status'),
            options: activeOptions,
          },
        ]}
        value={filters}
        onChange={setFilters}
      />
      {usersQuery.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(usersQuery.error)}
          onRetry={() => void usersQuery.refetch()}
        />
      )}
      {usersQuery.isPending && <LoadingState />}
      {usersQuery.isSuccess && (
        <UsersTable
          rows={usersQuery.data}
          onSelect={openEdit}
          emptyState={<EmptyState title={t('common:states.emptyTitle')} text={t('users:empty')} />}
          currentUserId={current?.id ?? ''}
          onEdit={openEdit}
          onDeactivate={openDeactivate}
          onReactivate={openReactivate}
          onResetPassword={openResetPassword}
          departmentsById={departmentsById}
        />
      )}
      {createOpen && <UserCreateModal onClose={() => setCreateOpen(false)} />}
      {drawerId && <UserEditDrawer id={drawerId} onClose={() => navigate('/users')} />}
      {deactivateTarget && (
        <UserDeactivateConfirm user={deactivateTarget} onClose={() => setDeactivateTarget(null)} />
      )}
      {reactivateTarget && (
        <UserReactivateConfirm user={reactivateTarget} onClose={() => setReactivateTarget(null)} />
      )}
      {resetTarget && (
        <UserResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      )}
    </div>
  );
}
